// constants.js
import { styled } from '@mui/material/styles';
import {
    TextField,
    TableContainer,
    Button,
    Card,
    IconButton,
    TableCell,
    TableRow,
    tableCellClasses,
    Chip,
    Dialog,
    FormControl,
    Paper,
    Tabs,
    Box,
    alpha,
    Typography,
    StepLabel
} from '@mui/material';
import Swal from 'sweetalert2';
import { FlightTakeoff, CheckCircle, Warning, Cancel, PendingActions, ReceiptLong } from '@mui/icons-material';

import { semantic, radius, shadow, density, motion, type, weight } from 'themes/tokens';

// Canonical navy. Previously this file declared its own primary (#2E4057)
// while the theme used #213363 - two different brand navies in one app.
export const globalColor = semantic.primary.main;

// Re-exported from `themes/tokens.js`. The shape is unchanged, so every
// existing consumer of `themeColors.*` keeps working; only the values are now
// shared with the MUI theme instead of drifting from it.
export const themeColors = semantic;

export const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: themeColors.primary.main,
        color: themeColors.primary.contrastText,
        fontWeight: 600
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: type.body1,
        padding: `${density.tableCellPaddingY}px ${density.tableCellPaddingX}px`
    }
}));

export const StyledTableRow = styled(TableRow)(() => ({
    '&:nth-of-type(odd)': {
        backgroundColor: themeColors.grey[50]
    },
    '&:hover': {
        backgroundColor: themeColors.grey[100]
    },
    transition: `background-color ${motion.quick}`,
    '&:last-of-type td, &:last-of-type th': {
        border: 0
    }
}));

export const renderStatusBadge = (status) => {
    const styles = {
        PENDING: {
            backgroundColor: themeColors.warning.lighter,
            color: themeColors.warning.dark,
            hover: themeColors.warning.light
        },
        AWAITING_APPROVAL: {
            backgroundColor: themeColors.warning.lighter,
            color: themeColors.warning.dark,
            hover: themeColors.warning.light
        },
        APPROVED: {
            backgroundColor: themeColors.success.lighter,
            color: themeColors.success.dark,
            hover: themeColors.success.light
        },
        PENDING_ACQUITTAL: {
            backgroundColor: themeColors.warning.lighter,
            color: themeColors.warning.dark,
            hover: themeColors.warning.light
        },
        COMPLETED: {
            backgroundColor: themeColors.success.lighter,
            color: themeColors.success.dark,
            hover: themeColors.success.light
        },
        DECLINED: {
            backgroundColor: themeColors.error.lighter,
            color: themeColors.error.dark,
            hover: themeColors.error.light
        },
        UNKNOWN: {
            backgroundColor: themeColors.grey[100],
            color: themeColors.grey[700],
            hover: themeColors.grey[200]
        }
    };

    const style = styles[status] || styles.UNKNOWN;
    const displayStatus = status?.replace(/_/g, ' ');

    return (
        <Chip
            label={displayStatus}
            sx={{
                backgroundColor: style.backgroundColor,
                color: style.color,
                fontWeight: weight.semibold,
                borderRadius: `${radius.sm}px`,
                '&:hover': {
                    backgroundColor: alpha(style.hover, 0.2)
                },
                transition: `background-color ${motion.quick}`,
                '& .MuiChip-label': {
                    px: 1
                }
            }}
        />
    );
};

export const colors = {
    primary: {
        main: themeColors.primary.main,
        light: themeColors.primary.lighter,
        gradient: `linear-gradient(135deg, ${themeColors.primary.main}, ${themeColors.primary.light})`
    },
    status: {
        pending: {
            bg: themeColors.warning.lighter,
            text: themeColors.warning.dark,
            gradient: `linear-gradient(135deg, ${themeColors.warning.main}, ${themeColors.warning.light})`
        },
        approved: {
            bg: themeColors.success.lighter,
            text: themeColors.success.dark,
            gradient: `linear-gradient(135deg, ${themeColors.success.main}, ${themeColors.success.light})`
        },
        declined: {
            bg: themeColors.error.lighter,
            text: themeColors.error.dark,
            gradient: `linear-gradient(135deg, ${themeColors.error.main}, ${themeColors.error.light})`
        },
        completed: {
            bg: themeColors.success.lighter,
            text: themeColors.success.dark,
            gradient: `linear-gradient(135deg, ${themeColors.success.main}, ${themeColors.success.light})`
        }
    }
};

// Styled Components
export const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: radius.lg,
    boxShadow: shadow.sm,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    // Was `overflow: hidden`, which clipped wide tables outright on mobile
    // instead of letting them scroll within their own container.
    maxWidth: '100%',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    [theme.breakpoints.down('sm')]: { borderRadius: radius.md },
    '& .MuiTableCell-head': {
        backgroundColor: themeColors.primary.main,
        color: themeColors.primary.contrastText,
        fontWeight: weight.semibold,
        fontSize: type.h6,
        padding: `${density.tableCellPaddingY}px ${density.tableCellPaddingX}px`
    }
}));

export const PaginationButton = styled(IconButton)(() => ({
    color: themeColors.primary.main,
    '&:hover': {
        backgroundColor: themeColors.primary.lighter
    },
    '&.Mui-disabled': {
        color: themeColors.grey[300]
    }
}));

export const StyledCard = styled(Card)(({ theme }) => ({
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: shadow.sm,
    borderRadius: radius.lg,
    transition: `box-shadow ${motion.smooth}, border-color ${motion.smooth}`,
    '&:hover': {
        boxShadow: shadow.md,
        borderColor: alpha(themeColors.primary.main, 0.35)
    }
}));

export const AnimatedButton = styled(Button)(() => ({
    background: themeColors.primary.main,
    borderRadius: radius.md,
    transition: `background-color ${motion.quick}, box-shadow ${motion.quick}`,
    '&:hover': {
        background: themeColors.primary.dark,
        boxShadow: shadow.sm
    }
}));

export const StyledTextField = styled(TextField)(() => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: radius.md,
        '&.Mui-focused fieldset': {
            borderColor: themeColors.primary.main
        }
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: themeColors.primary.main
    }
}));

export const StyledTabs = styled(Tabs)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? alpha(themeColors.grey[500], 0.16) : themeColors.grey[100],
    borderRadius: radius.md,
    padding: 4,
    minHeight: 38,
    '& .MuiTab-root': {
        borderRadius: radius.sm,
        minHeight: 30,
        padding: '6px 14px',
        fontWeight: weight.semibold,
        textTransform: 'none',
        color: theme.palette.text.secondary,
        '&.Mui-selected': {
            color: themeColors.primary.main,
            backgroundColor: theme.palette.background.paper,
            boxShadow: shadow.xs
        }
    },
    '& .MuiTabs-indicator': {
        display: 'none'
    }
}));

export const FilterSection = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : themeColors.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    padding: 16,
    borderRadius: radius.lg,
    marginBottom: 16,
    boxShadow: shadow.none,
    [theme.breakpoints.down('sm')]: { padding: 12, marginBottom: 12 }
}));

export const ActionButton = styled(Button)(() => ({
    fontWeight: weight.semibold,
    minWidth: 120,
    padding: `${density.buttonPaddingY}px ${density.buttonPaddingX}px`,
    borderRadius: radius.md,
    textTransform: 'none',
    boxShadow: 'none',
    transition: `background-color ${motion.quick}, box-shadow ${motion.quick}`,
    '&:hover': {
        boxShadow: shadow.sm
    }
}));

export const StyledFormControl = styled(FormControl)({
    '& .MuiOutlinedInput-root': {
        borderRadius: radius.md,
        '&:hover fieldset': {
            borderColor: themeColors.primary.main
        },
        '&.Mui-focused fieldset': {
            borderColor: themeColors.primary.main
        }
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: themeColors.primary.main
    }
});

export const PreviewDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: radius.lg,
        // `min()` rather than a flat 400px, which overflowed a 360px screen.
        minWidth: 'min(400px, 100%)',
        boxShadow: shadow.lg,
        [theme.breakpoints.down('sm')]: { minWidth: 0 }
    },
    '& .MuiDialogTitle-root': {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : themeColors.grey[50],
        color: themeColors.primary.main
    }
}));

export const InfoRow = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '10px',
    '& .label': {
        color: themeColors.primary.main,
        fontWeight: 600,
        minWidth: '120px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    '& .value': {
        flex: 1,
        color: themeColors.grey[700]
    }
});

export const StyledPaper = styled(Paper)(({ theme }) => ({
    width: '100%',
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: shadow.sm,
    borderRadius: radius.lg
}));

export const StyledTableHeaderCell = styled('th')(() => ({
    padding: `${density.tableCellPaddingY}px ${density.tableCellPaddingX}px`,
    textAlign: 'left',
    borderBottom: `1px solid ${themeColors.grey[200]}`,
    fontWeight: weight.semibold,
    backgroundColor: themeColors.primary.main,
    color: themeColors.primary.contrastText,
    fontSize: type.h6
}));

export const NumberTextField = styled('input')(() => ({
    width: '85px',
    textAlign: 'right',
    padding: '6px 10px',
    border: `1px solid ${themeColors.grey[200]}`,
    borderRadius: radius.sm,
    fontSize: type.body1,
    '&:focus': {
        outline: 'none',
        borderColor: themeColors.primary.main,
        boxShadow: shadow.focus
    }
}));

// Tabular numerals keep money columns aligned digit-for-digit.
export const AmountTypography = styled(Typography)({
    fontSize: type.body1,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: weight.medium,
    color: themeColors.grey[700]
});

export const TotalCell = styled(TableCell)(() => ({
    backgroundColor: alpha(themeColors.primary.main, 0.08),
    color: themeColors.primary.main,
    fontWeight: weight.semibold,
    fontSize: type.body1,
    fontVariantNumeric: 'tabular-nums'
}));

export const TextCell = styled(TableCell)({
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
});

// Sample data (keeping the existing data)
export const sampleData = [
    {
        type: 'Request',
        applicant: 'Tapiwa',
        tripDate: '23/09/2024',
        budget: 500,
        duration: '2 Days',
        purpose: 'Conference',
        status: 'Pending'
    }
    // ... rest of your sample data
];

// Keeping the existing handlers
const handleClosePreview = (setPreviewData) => {
    setPreviewData(null);
};

export const handleApproveTrip = (tripDate, setPreviewData) => {
    // Update the trip status in the data
    Swal.fire({
        title: 'Approved!',
        text: 'The trip has been approved.',
        icon: 'success'
    }).then(() => {
        handleClosePreview(setPreviewData);
    });
};

export const handleRejectTrip = (tripDate, setPreviewData) => {
    Swal.fire({
        title: 'Rejected!',
        text: 'The trip has been rejected.',
        icon: 'error'
    }).then(() => {
        handleClosePreview(setPreviewData);
    });
};

export const handleApproveAcquittal = (tripDate, setPreviewData) => {
    Swal.fire({
        title: 'Approved!',
        text: 'The acquittal has been approved.',
        icon: 'success'
    }).then(() => {
        handleClosePreview(setPreviewData);
    });
};

// Export theme object
export const theme = {
    primary: {
        main: themeColors.primary.main,
        light: themeColors.primary.lighter,
        dark: themeColors.primary.dark,
        contrastText: themeColors.primary.contrastText
    },
    background: {
        default: themeColors.background.default,
        paper: themeColors.background.paper,
        light: themeColors.grey[50]
    },
    text: {
        primary: themeColors.grey[900],
        secondary: themeColors.grey[600],
        light: themeColors.grey[400]
    },
    status: {
        success: themeColors.success.main,
        pending: themeColors.warning.main,
        inactive: themeColors.grey[500]
    },
    error: {
        main: themeColors.error.main,
        light: themeColors.error.lighter,
        dark: themeColors.error.dark,
        contrastText: themeColors.error.contrastText
    }
};

export const ExportButton = styled(Button)(() => ({
    backgroundColor: themeColors.success.main,
    color: themeColors.success.contrastText,
    padding: `${density.buttonPaddingY}px ${density.buttonPaddingX}px`,
    borderRadius: radius.md,
    fontWeight: weight.semibold,
    textTransform: 'none',
    transition: `background-color ${motion.quick}, box-shadow ${motion.quick}`,
    '&:hover': {
        backgroundColor: themeColors.success.dark,
        boxShadow: shadow.sm
    },
    gap: '8px'
}));

// Matches the 40px control height so it lines up with the search input
// it sits beside, rather than overhanging it at 56px.
export const SearchButton = styled(Button)(({ theme }) => ({
    backgroundColor: themeColors.primary.main,
    color: themeColors.primary.contrastText,
    height: density.controlHeight,
    marginLeft: '8px',
    // Sitting beside a search field, this has nowhere to go on a phone.
    [theme.breakpoints.down('sm')]: { marginLeft: 0, width: '100%' },
    padding: `0 ${density.buttonPaddingX}px`,
    borderRadius: radius.md,
    fontWeight: weight.semibold,
    textTransform: 'none',
    transition: `background-color ${motion.quick}, box-shadow ${motion.quick}`,
    '&:hover': {
        backgroundColor: themeColors.primary.dark,
        boxShadow: shadow.sm
    },
    gap: '8px'
}));

// ... (previous themeColors and other constants)

export const STATUS_CONFIG = {
    APPROVED: {
        bg: themeColors.success.main,
        text: themeColors.success.contrastText,
        border: themeColors.success.dark,
        gradient: `linear-gradient(135deg, ${themeColors.success.light} 0%, ${themeColors.success.dark} 100%)`,
        hover: `linear-gradient(135deg, ${themeColors.success.main} 0%, ${themeColors.success.dark} 100%)`,
        lightBg: themeColors.success.lighter,
        icon: <CheckCircle fontSize="small" />,
        chipBg: themeColors.success.lighter,
        triangle: `linear-gradient(135deg, ${themeColors.success.light} 0%, ${themeColors.success.main} 100%)`
    },
    PENDING: {
        bg: themeColors.warning.main,
        text: themeColors.warning.contrastText,
        border: themeColors.warning.dark,
        gradient: `linear-gradient(135deg, ${themeColors.warning.light} 0%, ${themeColors.warning.dark} 100%)`,
        hover: `linear-gradient(135deg, ${themeColors.warning.main} 0%, ${themeColors.warning.dark} 100%)`,
        lightBg: themeColors.warning.lighter,
        icon: <Warning fontSize="small" />,
        chipBg: themeColors.warning.lighter,
        triangle: `linear-gradient(135deg, ${themeColors.warning.light} 0%, ${themeColors.warning.main} 100%)`
    },
    REJECTED: {
        bg: themeColors.error.main,
        text: themeColors.error.contrastText,
        border: themeColors.error.dark,
        gradient: `linear-gradient(135deg, ${themeColors.error.light} 0%, ${themeColors.error.dark} 100%)`,
        hover: `linear-gradient(135deg, ${themeColors.error.main} 0%, ${themeColors.error.dark} 100%)`,
        lightBg: themeColors.error.lighter,
        icon: <Cancel fontSize="small" />,
        chipBg: themeColors.error.lighter,
        triangle: `linear-gradient(135deg, ${themeColors.error.light} 0%, ${themeColors.error.main} 100%)`
    },
    DEFAULT: {
        bg: themeColors.primary.main,
        text: themeColors.primary.contrastText,
        border: themeColors.primary.dark,
        gradient: `linear-gradient(135deg, ${themeColors.primary.light} 0%, ${themeColors.primary.dark} 100%)`,
        hover: `linear-gradient(135deg, ${themeColors.primary.main} 0%, ${themeColors.primary.dark} 100%)`,
        lightBg: themeColors.primary.lighter,
        icon: <FlightTakeoff fontSize="small" />,
        chipBg: themeColors.primary.lighter,
        triangle: `linear-gradient(135deg, ${themeColors.primary.light} 0%, ${themeColors.primary.main} 100%)`
    }
};

export const DASHBOARD_CARD_STYLES = {
    UPCOMING: {
        color: themeColors.primary.main,
        hoverColor: themeColors.primary.dark,
        icon: FlightTakeoff,
        gradientDark: themeColors.primary.dark,
        lightBg: alpha(themeColors.primary.main, 0.12),
        gradient: `linear-gradient(135deg, ${themeColors.primary.light} 0%, ${themeColors.primary.dark} 100%)`
    },
    PENDING: {
        color: themeColors.warning.main,
        hoverColor: themeColors.warning.dark,
        icon: PendingActions,
        gradientDark: themeColors.warning.dark,
        lightBg: alpha(themeColors.warning.main, 0.12),
        gradient: `linear-gradient(135deg, ${themeColors.warning.light} 0%, ${themeColors.warning.dark} 100%)`
    },
    ACQUITTALS: {
        color: themeColors.secondary.main,
        hoverColor: themeColors.secondary.dark,
        icon: ReceiptLong,
        gradientDark: themeColors.secondary.dark,
        lightBg: alpha(themeColors.secondary.main, 0.12),
        gradient: `linear-gradient(135deg, ${themeColors.secondary.light} 0%, ${themeColors.secondary.dark} 100%)`
    },
    COMPLETED: {
        color: themeColors.success.main,
        hoverColor: themeColors.success.dark,
        icon: CheckCircle,
        gradientDark: themeColors.success.dark,
        lightBg: alpha(themeColors.success.main, 0.12),
        gradient: `linear-gradient(135deg, ${themeColors.success.light} 0%, ${themeColors.success.dark} 100%)`
    },
    DEFAULT: {
        bg: themeColors.primary.main,
        text: themeColors.primary.contrastText,
        border: themeColors.primary.dark,
        gradient: `linear-gradient(135deg, ${themeColors.primary.light} 0%, ${themeColors.primary.dark} 100%)`,
        hover: `linear-gradient(135deg, ${themeColors.primary.main} 0%, ${themeColors.primary.dark} 100%)`,
        lightBg: alpha(themeColors.primary.main, 0.12),
        icon: <FlightTakeoff fontSize="small" />,
        chipBg: themeColors.primary.lighter,
        triangle: `linear-gradient(135deg, ${themeColors.primary.light} 0%, ${themeColors.primary.main} 100%)`
    }
};

export const GreenStepLabel = styled(StepLabel)(({ theme }) => ({
    '& .MuiStepLabel-label': {
        color: theme.palette.text.secondary
    },
    '& .MuiStepLabel-label.Mui-active': {
        color: globalColor,
        fontWeight: 'bold'
    },
    '& .MuiStepLabel-label.Mui-completed': {
        color: globalColor
    },
    '& .MuiStepIcon-root': {
        color: theme.palette.grey[300]
    },
    '& .MuiStepIcon-root.Mui-active': {
        color: globalColor
    },
    '& .MuiStepIcon-root.Mui-completed': {
        color: globalColor
    }
}));
